// components/Header.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';  // ← Added

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: ${props => (props.$scrolled ? 'rgba(0, 0, 0, 0.95)' : 'transparent')};
  backdrop-filter: ${props => (props.$scrolled ? 'blur(10px)' : 'none')};
  border-bottom: ${props => (props.$scrolled ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')};
  transition: all 0.3s ease;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const Logo = styled.div`
  width: 300px;
  height: auto;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 160px;
  }

  @media (max-width: 480px) {
    width: 250px;
  }
`;

const NavLinks = styled(motion.ul)`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    display: ${props => (props.$isOpen ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: rgba(0, 0, 0, 0.98);
    flex-direction: column;
    padding: 2rem;
    gap: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const NavLink = styled(motion.li)`
  a {
    color: white;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.80rem;
    transition: color 0.3s ease;
    cursor: pointer;

    &:hover {
      color: #C8AB48;
    }
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

// Animation only for header slide-in (logo no longer animated)
const headerVariants = {
  hidden: { y: -100 },
  visible: { y: 0 },
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();  // ← For navigating to "/"

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      window.scrollTo({
        top: element.offsetTop - offset,
        behavior: 'smooth',
      });
    }
    setMobileMenuOpen(false);
  };

  const goHome = () => {
    navigate('/');           // ← Navigates to home page
    window.scrollTo(0, 0);   // ← Scroll to top
    setMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Who We Are', id: 'who-we-are' },
    { name: 'How We Work', id: 'how-we-work' },
    { name: 'Services', id: 'services' },
    { name: 'Testimonials', id: 'testimonials' },
    { name: 'CV Sessions', id: 'cv-sessions' },
    { name: 'Founder Story', id: 'founder-story' },
    { name: 'Contact Us', id: 'get-in-touch' },
  ];

  return (
    <HeaderContainer
      initial="hidden"
      animate="visible"
      variants={headerVariants}
      transition={{ duration: 0.6 }}
      $scrolled={scrolled}
    >
      <Nav>
        {/* Logo – clean, no hover effect, clicks to home */}
        <Logo onClick={goHome}>
          <img 
            src="/SSDLogo.svg" 
            alt="Start Something Different Logo" 
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Logo>

        <NavLinks $isOpen={mobileMenuOpen}>
          {navItems.map((item) => (
            <NavLink key={item.id}>
              <a onClick={() => scrollToSection(item.id)}>{item.name}</a>
            </NavLink>
          ))}
        </NavLinks>

        <MobileMenuButton
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          whileTap={{ scale: 0.95 }}
        >
          {mobileMenuOpen ? '×' : '☰'}
        </MobileMenuButton>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;